import { Engine, ClipTypeEnum } from "@rendley/sdk";
import { ApplicationStore } from "../../../store/ApplicationStore";
import {CLIP_LIBRETTO_CLIP_ID} from "../../../Constants";

const SNAP_TOLERANCE = 10;

interface ComputedGuidelines {
    verticalGuidelines: number[];
    horizontalGuidelines: number[];
}

const useRef = <T = any>(value: T) => {
    return {
        current: value,
    };
};

class CanvasGuidelinesManager {
    computedGuidelinesRef = useRef<ComputedGuidelines>({
        verticalGuidelines: [],
        horizontalGuidelines: [],
    });

    prepareGuidelines = () => {
        const resolution = Engine.getInstance().getDisplay().getResolution();

        const [displayWidth, displayHeight] = resolution;
        const clips = Engine.getInstance().getTimeline().getClips();
        const selectedLibrettoClipId = ApplicationStore.selectedLibrettoClipId;

        const screenCenterX = displayWidth / 2;
        const screenCenterY = displayHeight / 2;

        const verticalGuidelines = new Set<number>();
        const horizontalGuidelines = new Set<number>();

        verticalGuidelines.add(0);
        verticalGuidelines.add(screenCenterX);
        verticalGuidelines.add(displayWidth);

        horizontalGuidelines.add(0);
        horizontalGuidelines.add(screenCenterY);
        horizontalGuidelines.add(displayHeight);

        for (let i = 0; i < clips.length; i++) {
            const clip = clips[i];

            // Skip selected clip
            if (clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) === selectedLibrettoClipId) {
                continue;
            }

            // Skip subtitle clips - they don't have style.getPosition()
            if (clip.getType() === ClipTypeEnum.SUBTITLES || clip.getType() === ClipTypeEnum.AUDIO) {
                continue;
            }

            const [x, y] = clip.style.getPosition();
            const w = clip.style.getWidth();
            const h = clip.style.getHeight();
            const rotation = clip.style.getRotation();

            if (rotation === 0) {
                verticalGuidelines.add(x - w / 2); // left
                verticalGuidelines.add(x); // center
                verticalGuidelines.add(x + w / 2); // right

                horizontalGuidelines.add(y - h / 2); // top
                horizontalGuidelines.add(y); // center
                horizontalGuidelines.add(y + h / 2); // bottom
            } else {
                const corners = [
                    { x: x - w / 2, y: y - h / 2 }, // top left
                    { x: x + w / 2, y: y - h / 2 }, // top right
                    { x: x + w / 2, y: y + h / 2 }, // bottom right
                    { x: x - w / 2, y: y + h / 2 }, // bottom left
                ];

                corners.forEach((pt) => {
                    const dx = pt.x - x;
                    const dy = pt.y - y;

                    const rotatedX = x + dx * Math.cos(rotation) - dy * Math.sin(rotation);
                    const rotatedY = y + dx * Math.sin(rotation) + dy * Math.cos(rotation);

                    verticalGuidelines.add(rotatedX);
                    horizontalGuidelines.add(rotatedY);
                });

                verticalGuidelines.add(x);
                horizontalGuidelines.add(y);
            }
        }

        this.computedGuidelinesRef.current = {
            verticalGuidelines: Array.from(verticalGuidelines),
            horizontalGuidelines: Array.from(horizontalGuidelines),
        };
    };

    resetGuidelines = () => {
        this.computedGuidelinesRef.current = {
            verticalGuidelines: [],
            horizontalGuidelines: [],
        };
    };

    getVerticalGuideline = (positionX: number, width: number): number | null => {
        const guidelines = this.computedGuidelinesRef.current.verticalGuidelines;

        const left = positionX - width / 2;
        const center = positionX;
        const right = positionX + width / 2;

        let closestGuideline: number | null = null;
        let smallestDistance = SNAP_TOLERANCE;

        guidelines.forEach((guideline) => {
            const guidelinePosition = guideline;

            const leftDist = Math.abs(guidelinePosition - left);
            if (leftDist < smallestDistance) {
                smallestDistance = leftDist;
                closestGuideline = guideline;
            }

            const centerDist = Math.abs(guidelinePosition - center);
            if (centerDist < smallestDistance) {
                smallestDistance = centerDist;
                closestGuideline = guideline;
            }

            const rightDist = Math.abs(guidelinePosition - right);
            if (rightDist < smallestDistance) {
                smallestDistance = rightDist;
                closestGuideline = guideline;
            }
        });

        return closestGuideline;
    };

    getHorizontalGuideline = (positionY: number, height: number): number | null => {
        const guidelines = this.computedGuidelinesRef.current.horizontalGuidelines;

        const top = positionY - height / 2;
        const center = positionY;
        const bottom = positionY + height / 2;

        let closestGuideline: number | null = null;
        let smallestDistance = SNAP_TOLERANCE;

        guidelines.forEach((guideline) => {
            const guidelinePosition = guideline;

            const topDist = Math.abs(guidelinePosition - top);
            if (topDist < smallestDistance) {
                smallestDistance = topDist;
                closestGuideline = guideline;
            }

            const centerDist = Math.abs(guidelinePosition - center);
            if (centerDist < smallestDistance) {
                smallestDistance = centerDist;
                closestGuideline = guideline;
            }

            const bottomDist = Math.abs(guidelinePosition - bottom);
            if (bottomDist < smallestDistance) {
                smallestDistance = bottomDist;
                closestGuideline = guideline;
            }
        });

        return closestGuideline;
    };

    applyGuidelineSnapping = (position: number, value: number, guideline: number | null): number => {
        if (guideline === null) {
            return position;
        }

        const guidelinePosition = guideline;
        const left = position - value / 2;
        const right = position + value / 2;

        const leftDist = Math.abs(guidelinePosition - left);
        const centerDist = Math.abs(guidelinePosition - position);
        const rightDist = Math.abs(guidelinePosition - right);

        if (leftDist <= SNAP_TOLERANCE && leftDist <= centerDist && leftDist <= rightDist) {
            return guidelinePosition + value / 2;
        } else if (rightDist <= SNAP_TOLERANCE && rightDist <= centerDist && rightDist <= leftDist) {
            return guidelinePosition - value / 2;
        } else if (centerDist <= SNAP_TOLERANCE) {
            return guidelinePosition;
        }

        return position;
    };
}

export default CanvasGuidelinesManager;
